/**
 * mail_searchMessages Command
 * 
 * Search email messages across integrations
 */

import { SimpleDocCommand } from '../../../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../../types/commands';
import useEntityStore from '../../../../stores/entityStore';
import type { MailThreadEntity } from '../../types/entities';

export const mailSearchMessagesCommand: SimpleDocCommand = {
  id: 'mail_searchMessages',
  logicalName: 'mail_searchMessages',
  version: 'v1',
  
  description: `Search email threads by subject, sender, or content.

**SEARCH SCOPE:**
Searches Mail.Thread entities in the local entity store.

**EXAMPLES:**

Search all mail:
{
  "query": "meeting"
}

Search specific integration:
{
  "query": "project update",
  "integrationId": "integration_123",
  "limit": 20
}`,
  
  schema: {
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'Search query (searches subject, sender, and snippet)',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'query is required';
          }
          if (value.length > 500) {
            return 'Query must be less than 500 characters';
          }
          return null;
        }
      },
      {
        name: 'integrationId',
        type: 'string',
        description: 'Mail integration ID (optional, searches all if not specified)',
        required: false,
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum results to return (default: 50)',
        required: false,
        validator: (value: number) => {
          if (value && (value < 1 || value > 200)) {
            return 'limit must be between 1 and 200';
          }
          return null;
        }
      },
    ],
  },
  
  examples: {
    good: [
      {
        input: { query: 'meeting' },
        expectedOutput: createSuccessResult(
          'Found 5 threads',
          {
            threads: [
              { id: 'thread_123', subject: 'Team Meeting Notes', snippet: '...' },
            ],
            count: 5
          }
        ),
        description: 'Search for "meeting" across all accounts',
      },
      {
        input: { 
          query: 'project update',
          integrationId: 'integration_123',
          limit: 10
        },
        expectedOutput: createSuccessResult(
          'Found 3 threads',
          {
            threads: [
              { id: 'thread_456', subject: 'Project Update Q1', snippet: '...' },
            ],
            count: 3
          }
        ),
        description: 'Search specific integration with limit',
      },
    ],
    bad: [
      {
        input: { query: '' },
        error: 'query is required',
        why: 'Empty query provided',
        fix: 'Provide a search query string'
      }
    ],
  },
  
  formatViewData: (result: any, toolInput: any) => {
    if (result.status === 'success') {
      return {
        type: 'mail_search',
        summary: result.message,
        data: result.data,
        query: toolInput.query
      };
    }
    return null;
  },
  
  handler: async (
    params: { query: string; integrationId?: string; limit?: number },
    context: CommandExecutionContext
  ): Promise<StandardToolResult> => {
    try {
      const query = params.query.trim();
      if (!query) {
        return createErrorResult(
          'Empty query',
          'Please provide a search query',
          'validation'
        );
      }
      
      const limit = params.limit || 50;
      
      // Get all threads from entity store
      const entityState = useEntityStore.getState();
      const allThreads = entityState.entities.filter(
        (e: any) => e.entityType === 'Mail.Thread'
      ) as MailThreadEntity[];
      
      // Filter by search query
      const queryLower = query.toLowerCase();
      let threads = allThreads.filter(thread => {
        const matchesQuery = 
          thread.name.toLowerCase().includes(queryLower) ||
          thread.skeleton.snippet?.toLowerCase().includes(queryLower) ||
          thread.skeleton.participants.some(p => 
            p.email.toLowerCase().includes(queryLower) ||
            p.name?.toLowerCase().includes(queryLower)
          );
        
        const matchesIntegration = !params.integrationId || 
          thread.skeleton.integrationId === params.integrationId;
        
        return matchesQuery && matchesIntegration;
      });
      
      // Limit results
      threads = threads.slice(0, limit);
      
      const results = threads.map(t => ({
        id: t._id,
        subject: t.name,
        snippet: t.skeleton.snippet,
        participants: t.skeleton.participants.map(p => p.email),
        messageCount: t.skeleton.messageCount,
        lastMessageAt: t.skeleton.lastMessageAt,
        hasUnread: t.skeleton.hasUnread,
      }));
      
      return createSuccessResult(
        `Found ${results.length} thread${results.length === 1 ? '' : 's'}`,
        { threads: results, count: results.length }
      );
    } catch (error) {
      return createErrorResult(
        'Search failed',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  },
};
